import { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { History, Loader2, ShieldOff, Mail, MailCheck, MailX, Users, ArrowLeft, UserCheck, CheckCircle2 } from 'lucide-react';

const fmtDate = (d) => d ? new Date(d).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const EmailStatus = ({ b }) => {
    if (!b.emails_requested) return <span className="text-slate-500">Sin envío de correos</span>;
    if (!b.emails_finished_at) return <span className="text-amber-300 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Enviando… {b.emails_sent || 0}/{b.disabled_count}</span>;
    return (
        <span className="inline-flex items-center gap-2">
            <span className="text-emerald-300 inline-flex items-center gap-1"><MailCheck className="w-3 h-3" /> {b.emails_sent} enviados</span>
            {b.emails_failed > 0 && <span className="text-red-300 inline-flex items-center gap-1"><MailX className="w-3 h-3" /> {b.emails_failed} fallidos</span>}
        </span>
    );
};

const Stat = ({ label, value, cls = 'text-white' }) => (
    <div className="rounded-lg bg-slate-950 border border-slate-800 px-3 py-2 text-center">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        <p className={`text-lg font-bold tabular-nums ${cls}`}>{value}</p>
    </div>
);

const BatchDetail = ({ batchId, onBack, onReactivated }) => {
    const [b, setB] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [confirmAll, setConfirmAll] = useState(false);
    const [busyAll, setBusyAll] = useState(false);
    const load = () => adminAPI.disableBatchDetail(batchId).then((r) => setB(r.data)).catch(() => toast.error('No se pudo cargar el lote'));
    useEffect(() => { load(); }, [batchId]); // eslint-disable-line react-hooks/exhaustive-deps

    const reactivate = async (uid) => {
        setBusyId(uid);
        try { await adminAPI.reactivateUser(uid); toast.success('Cuenta reactivada'); load(); onReactivated?.(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Error al reactivar'); }
        finally { setBusyId(null); }
    };

    const reactivateAll = async () => {
        setBusyAll(true);
        try {
            const { data } = await adminAPI.reactivateBatch(batchId);
            toast.success(`${data.reactivated_count} cuentas reactivadas · ya pueden iniciar sesión`);
            setConfirmAll(false); load(); onReactivated?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error en la reactivación masiva'); }
        finally { setBusyAll(false); }
    };

    if (!b) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#7CB1E5]" /></div>;
    return (
        <div className="space-y-4" data-testid="disable-batch-detail">
            <button type="button" onClick={onBack} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid="disable-batch-back"><ArrowLeft className="w-3 h-3" /> Volver al historial</button>
            <div>
                <p className="text-white font-semibold">{fmtDate(b.created_at)}</p>
                <p className="text-xs text-slate-500">Ejecutado por {b.admin_email} · Lote {b.id.slice(0, 8)}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <Stat label="Inhabilitadas" value={b.disabled_count} cls="text-red-300" />
                <Stat label="Activas tras lote" value={b.remaining_active_count} cls="text-emerald-300" />
                <Stat label="Excluidas" value={b.excluded_count} />
                <Stat label="Siguen inhabilitadas" value={b.still_disabled_count} cls="text-red-300" />
                <Stat label="Reactivadas" value={b.reactivated_count} cls="text-emerald-300" />
            </div>
            <p className="text-xs"><EmailStatus b={b} /></p>

            {b.still_disabled_count > 0 ? (
                <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3 space-y-2" data-testid="batch-reactivate-all-box">
                    {!confirmAll ? (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs text-emerald-100">{b.still_disabled_count} cuentas de este lote siguen inhabilitadas.</p>
                            <Button size="sm" onClick={() => setConfirmAll(true)} className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="batch-reactivate-all-btn">
                                <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Reactivar todas ({b.still_disabled_count})
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs text-emerald-100">¿Confirmas reactivar las <strong>{b.still_disabled_count}</strong> cuentas? Podrán iniciar sesión de inmediato y recibirán una notificación.</p>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setConfirmAll(false)} disabled={busyAll} className="h-8 border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="batch-reactivate-all-cancel">Cancelar</Button>
                                <Button size="sm" onClick={reactivateAll} disabled={busyAll} className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="batch-reactivate-all-confirm">
                                    {busyAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Sí, reactivar todas</>}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            ) : b.mass_reactivated_at ? (
                <p className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-3 py-2" data-testid="batch-mass-reactivated-info">
                    Reactivación masiva ejecutada el {fmtDate(b.mass_reactivated_at)} por {b.mass_reactivated_by} · {b.mass_reactivated_count} cuentas
                </p>
            ) : null}

            <div>
                <p className="text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1"><ShieldOff className="w-3.5 h-3.5 text-red-400" /> Usuarios afectados ({b.affected_users?.length || 0})</p>
                <div className="rounded-xl bg-slate-950 border border-slate-800 max-h-[32vh] overflow-y-auto divide-y divide-slate-800/60" data-testid="disable-batch-affected">
                    {(b.affected_users || []).map((u) => (
                        <div key={u.id} className="px-3 py-2 flex items-center gap-3 text-xs" data-testid={`disable-batch-user-${u.id}`}>
                            <div className="flex-1 min-w-0">
                                <p className="text-slate-200 truncate">{u.name || '—'}</p>
                                <p className="text-slate-500 truncate">{u.email}</p>
                            </div>
                            {u.current_status === 'disabled' ? (
                                <>
                                    <span className="px-2 py-0.5 rounded-md bg-red-500/15 text-red-300 font-semibold">Inhabilitado</span>
                                    <Button size="sm" onClick={() => reactivate(u.id)} disabled={busyId === u.id} className="h-7 px-2 bg-emerald-500 hover:bg-emerald-600 text-[11px]" data-testid={`disable-batch-reactivate-${u.id}`}>
                                        {busyId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3 mr-1" />Reactivar</>}
                                    </Button>
                                </>
                            ) : (
                                <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-300 font-semibold" title={u.reactivated_by ? `Por ${u.reactivated_by} · ${fmtDate(u.reactivated_at)}` : ''}>
                                    {u.current_status === 'deleted' ? 'Eliminado' : `Reactivado${u.reactivated_at ? ' · ' + fmtDate(u.reactivated_at) : ''}`}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            {b.excluded_users?.length > 0 && (
                <div>
                    <p className="text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1"><UserCheck className="w-3.5 h-3.5 text-emerald-400" /> Excluidos (permanecieron activos) ({b.excluded_users.length})</p>
                    <div className="rounded-xl bg-slate-950 border border-slate-800 max-h-40 overflow-y-auto divide-y divide-slate-800/60" data-testid="disable-batch-excluded">
                        {b.excluded_users.slice(0, 500).map((u) => (
                            <div key={u.id} className="px-3 py-1.5 flex justify-between gap-2 text-xs"><span className="text-slate-200 truncate">{u.name || '—'}</span><span className="text-slate-500 truncate">{u.email}</span></div>
                        ))}
                        {b.excluded_users.length > 500 && <p className="text-center text-slate-500 text-[11px] py-1.5">Mostrando 500 de {b.excluded_users.length}</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

export const DisableBatchesDialog = ({ open, onOpenChange, onReactivated }) => {
    const [batches, setBatches] = useState(null);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!open) return;
        setSelected(null); setBatches(null);
        adminAPI.disableBatches().then((r) => setBatches(r.data.batches || [])).catch(() => toast.error('No se pudo cargar el historial'));
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-white max-h-[90vh] overflow-y-auto" data-testid="disable-batches-dialog">
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2"><History className="w-5 h-5 text-[#7CB1E5]" /> Historial de inhabilitaciones masivas</DialogTitle>
                </DialogHeader>
                {selected ? <BatchDetail batchId={selected} onBack={() => setSelected(null)} onReactivated={onReactivated} /> : !batches ? (
                    <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#7CB1E5]" /></div>
                ) : batches.length === 0 ? (
                    <p className="text-center text-slate-500 text-sm py-10" data-testid="disable-batches-empty">Aún no se ha ejecutado ninguna inhabilitación masiva.</p>
                ) : (
                    <div className="divide-y divide-slate-800/60 rounded-xl bg-slate-950 border border-slate-800" data-testid="disable-batches-list">
                        {batches.map((b) => (
                            <button key={b.id} type="button" onClick={() => setSelected(b.id)} className="w-full text-left px-4 py-3 hover:bg-slate-800/40 transition-colors" data-testid={`disable-batch-${b.id}`}>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-white text-sm font-semibold">{fmtDate(b.created_at)}</p>
                                    <p className="text-[11px] text-slate-500">por {b.admin_email}</p>
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs">
                                    <span className="text-red-300 inline-flex items-center gap-1"><ShieldOff className="w-3 h-3" /> {b.disabled_count} inhabilitadas</span>
                                    <span className="text-emerald-300 inline-flex items-center gap-1"><Users className="w-3 h-3" /> {b.remaining_active_count} activas</span>
                                    <span className="text-slate-400">{b.excluded_count} excluidas</span>
                                    <span className="text-slate-400">{b.reactivated_count} reactivadas desde entonces</span>
                                    <EmailStatus b={b} />
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
